import {
  healthResponseSchema,
  readinessResponseSchema,
  type DependencyStatus,
  type HealthResponse,
  type ReadinessResponse,
} from '@unibyte/contracts';
import useSWR from 'swr';
import { fetchJson, getApiBaseUrl } from '../lib/api.js';
import {
  Badge,
  Card,
  ErrorNote,
  Icon,
  type IconName,
  PageHeader,
  Skeleton,
  Table,
  Td,
} from '../components/ui/index.js';
import { useRemote } from '../lib/remote.js';
import { useAdminLocale } from '../lib/admin-locale.js';

const REFRESH_MS = 10_000;

type PlatformSettings = {
  provider?: {
    kind?: string | null;
    model?: string | null;
    keyConfigured?: boolean;
  };
  smtp?: {
    host?: string | null;
    port?: number | null;
    user?: string | null;
    mailFrom?: string | null;
    passwordSet?: boolean;
  };
};

type ApplicationSettings = {
  items?: Array<{ key: string; value: string | null }>;
};

type StatusValue = 'up' | 'down' | 'ok' | 'ready' | 'degraded';

function StatusBadge({ status }: { status: StatusValue }) {
  const { text } = useAdminLocale();
  const labels: Record<'up' | 'down' | 'ok' | 'ready' | 'degraded', string> = {
    up: text('Ενεργό', 'Up'),
    ok: text('Ενεργό', 'Up'),
    ready: text('Έτοιμο', 'Ready'),
    down: text('Μη διαθέσιμο', 'Unavailable'),
    degraded: text('Υποβαθμισμένο', 'Degraded'),
  };
  return <Badge value={status} label={labels[status]} />;
}

function DependencyRow({
  name,
  icon,
  check,
}: {
  name: string;
  icon: IconName;
  check: DependencyStatus | undefined;
}) {
  if (!check) return null;
  const tone =
    check.status === 'down'
      ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-600'
      : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600';
  return (
    <tr>
      <Td>
        <span className="flex items-center gap-3">
          <span className={`grid size-9 shrink-0 place-items-center rounded-full ${tone}`}>
            <Icon name={icon} className="size-4" />
          </span>
          <span className="font-medium text-slate-900 dark:text-slate-100">{name}</span>
        </span>
      </Td>
      <Td>
        <StatusBadge status={check.status} />
      </Td>
      <Td muted>{check.latencyMs !== undefined ? `${check.latencyMs} ms` : '—'}</Td>
      <Td muted>{check.detail ?? '—'}</Td>
    </tr>
  );
}

export function SystemStatusPage() {
  const { locale, text } = useAdminLocale();
  const health = useSWR<HealthResponse>(
    'health',
    () => fetchJson('/health', healthResponseSchema),
    { refreshInterval: REFRESH_MS },
  );
  const ready = useSWR<ReadinessResponse>(
    'ready',
    () => fetchJson('/ready', readinessResponseSchema),
    { refreshInterval: REFRESH_MS },
  );
  const platform = useRemote<PlatformSettings>('/v1/platform/settings', null);
  const application = useRemote<ApplicationSettings>('/v1/platform/application-settings', null);
  const configuredFlags = (application.data?.items ?? []).filter(
    (item) => item.value !== null && item.value !== '',
  );

  return (
    <section className="admin-page">
      <PageHeader
        description={text(
          `Ανανέωση κάθε ${REFRESH_MS / 1000} δευτερόλεπτα.`,
          `Refreshes every ${REFRESH_MS / 1000} seconds.`,
        )}
        actions={
          <code className="rounded-[var(--admin-radius-sm)] border border-[color:var(--admin-border)] bg-[color:var(--admin-surface)] px-2.5 py-1.5 text-xs text-slate-600 dark:text-slate-400">
            {getApiBaseUrl()}
          </code>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card
          title="Liveness"
          description="GET /health"
          actions={health.data ? <StatusBadge status={health.data.status} /> : null}
        >
          {health.isLoading ? <Skeleton /> : null}
          {health.error ? (
            <ErrorNote message={text('Το API δεν απαντά.', 'The API is not responding.')} />
          ) : null}
          {health.data ? (
            <p className="admin-mono text-2xl font-semibold text-slate-950 dark:text-white">
              {health.data.version}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-slate-500">{text('Έκδοση API', 'API version')}</p>
        </Card>
        <Card
          title="Uptime"
          description={text('Από την τελευταία εκκίνηση', 'Since the last startup')}
        >
          <p className="admin-mono text-2xl font-semibold text-slate-950 dark:text-white">
            {health.data ? `${health.data.uptimeSeconds}s` : '—'}
          </p>
          <p className="mt-1 text-xs text-slate-500">{text('Χρόνος λειτουργίας', 'Uptime')}</p>
        </Card>
        <Card
          title="Readiness"
          description="GET /ready"
          actions={ready.data ? <StatusBadge status={ready.data.status} /> : null}
        >
          <p className="admin-mono text-2xl font-semibold text-slate-950 dark:text-white">
            {ready.data
              ? `${Object.values(ready.data.checks).filter((check) => check?.status === 'up').length}/${Object.keys(ready.data.checks).length}`
              : '—'}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {text('Εξαρτήσεις διαθέσιμες', 'Dependencies available')}
          </p>
        </Card>
        <Card title="Last check" description={text('Αυτόματη ανανέωση', 'Automatic refresh')}>
          <p className="admin-mono text-2xl font-semibold text-slate-950 dark:text-white">
            {health.data
              ? new Date(health.data.timestamp).toLocaleTimeString(
                  locale === 'en' ? 'en-GB' : 'el-GR',
                )
              : '—'}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {text('Κάθε', 'Every')} {REFRESH_MS / 1000}s
          </p>
        </Card>
      </div>

      {health.error ? (
        <ErrorNote
          message={text(
            'Το API δεν απαντά. Ελέγξτε ότι τρέχει το pnpm dev.',
            'The API is not responding. Check that pnpm dev is running.',
          )}
        />
      ) : null}

      <Card
        title="Readiness"
        description="GET /ready"
        actions={ready.data ? <StatusBadge status={ready.data.status} /> : null}
        flush={Boolean(ready.data)}
      >
        {ready.isLoading ? <Skeleton /> : null}
        {ready.error ? (
          <ErrorNote
            message={text(
              'Δεν ήταν δυνατή η ανάγνωση της ετοιμότητας.',
              'Could not read readiness status.',
            )}
          />
        ) : null}
        {ready.data ? (
          <Table
            head={[
              text('Εξάρτηση', 'Dependency'),
              text('Κατάσταση', 'Status'),
              text('Καθυστέρηση', 'Latency'),
              text('Λεπτομέρεια', 'Details'),
            ]}
          >
            <DependencyRow
              name="PostgreSQL + pgvector"
              icon="server"
              check={ready.data.checks.database}
            />
            <DependencyRow name="Redis" icon="pulse" check={ready.data.checks.redis} />
            <DependencyRow name="Queue" icon="inbox" check={ready.data.checks.queue} />
          </Table>
        ) : null}
      </Card>

      <div className="grid items-start gap-4 xl:grid-cols-2">
        <Card
          title="Runtime configuration"
          description={text(
            'Ενεργές ρυθμίσεις provider χωρίς έκθεση μυστικών.',
            'Active provider settings without exposing secrets.',
          )}
          actions={platform.error ? <StatusBadge status="down" /> : <StatusBadge status="up" />}
        >
          {platform.isLoading ? <Skeleton /> : null}
          {platform.error ? (
            <ErrorNote
              message={text(
                'Δεν ήταν δυνατή η ανάγνωση των ρυθμίσεων.',
                'Could not read the settings.',
              )}
            />
          ) : null}
          {platform.data ? (
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500">AI provider</dt>
                <dd className="admin-mono mt-1 font-medium uppercase text-slate-900 dark:text-slate-100">
                  {platform.data.provider?.kind ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Chat model</dt>
                <dd className="admin-mono mt-1 truncate font-medium text-slate-900 dark:text-slate-100">
                  {platform.data.provider?.model ?? text('Δεν έχει οριστεί', 'Not set')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Provider key</dt>
                <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">
                  {platform.data.provider?.keyConfigured ? 'Configured' : 'Not configured'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">SMTP</dt>
                <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">
                  {platform.data.smtp?.host
                    ? `${platform.data.smtp.host}:${platform.data.smtp.port ?? ''}`
                    : 'Not configured'}
                </dd>
              </div>
            </dl>
          ) : null}
        </Card>

        <Card
          title="Application settings"
          description={text(
            'Ρυθμίσεις λειτουργίας που έχουν αποθηκευτεί στην πλατφόρμα.',
            'Operational settings saved on the platform.',
          )}
          actions={
            <span className="admin-mono text-xs text-slate-500">
              {configuredFlags.length} configured
            </span>
          }
        >
          {application.isLoading ? <Skeleton /> : null}
          {application.error ? (
            <ErrorNote
              message={text(
                'Δεν ήταν δυνατή η ανάγνωση των application settings.',
                'Could not read the application settings.',
              )}
            />
          ) : null}
          {application.data ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {application.data.items?.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-3 border-b border-[color:var(--admin-border-soft)] py-2 text-sm last:border-0"
                >
                  <span className="admin-mono truncate text-[10px] tracking-[0.04em] text-slate-500">
                    {item.key}
                  </span>
                  <Badge
                    value={item.value ? 'ACTIVE' : 'DISABLED'}
                    label={item.value ? 'set' : 'empty'}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </Card>
      </div>
    </section>
  );
}
